import type { ChangeEvent, MouseEvent, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { IconEmail } from '@gto/ui/assets'
import { Button } from '@gto/ui/button'
import { Grow } from '@gto/ui/grow'
import { EnvelopeIcon } from '@gto/ui/icons/solid'
import { Typography } from '@gto/ui/typography'

import { useAccountEmailChangeCreate, useAccountEmailVerifyResendCreate } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { Loader } from '@/components/loader'
import { TextInput } from '@/components/text-input'
import { useAuth } from '@/hooks/use-auth'
import { useFormValidation } from '@/hooks/use-form-validation'
import { ChangeEmailModal } from '@/pages/account/components/change-email/change-email-modal'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { PasswordResetFormData, PasswordResetFormField } from '@/utils/validation'
import { passwordResetSchema } from '@/utils/validation'

const APPLE_PRIVATE_RELAY_DOMAIN = '@privaterelay.appleid.com'

// Helper to check if verification email was sent within the last hour
const isWithinOneHour = (sentAt: string | null): boolean => {
  if (sentAt === null) {
    return false
  }

  const sentDate = new Date(sentAt)
  const now = new Date()
  const diffMs = now.getTime() - sentDate.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  return diffHours < 1
}

export const EmailVerificationForm = () => {
  const { formatMessage } = useIntl()
  const { user, isInitialized } = useAuth()
  const { pathname } = useLocation()
  const { validate, errors, validateField } = useFormValidation(passwordResetSchema)
  const [status, setStatus] = useState<{
    type: 'success' | 'error'
    message?: string | ReactNode
  } | null>(null)
  const [formValues, setFormValues] = useState<PasswordResetFormData>(() => {
    let initialEmail = ''
    if (user?.email != null) {
      initialEmail = user.email.endsWith(APPLE_PRIVATE_RELAY_DOMAIN) ? '' : user.email
    }

    return {
      email: initialEmail,
    }
  })
  const [showModal, setShowModal] = useState(false)

  const { mutate: changeEmail, isPending } = useAccountEmailChangeCreate({
    mutation: {
      onSuccess: () => {
        setStatus({
          type: 'success',
        })
      },
      onError: (err: AxiosError) => {
        setStatus({
          type: 'error',
          message:
            getAxiosErrorMessage(err) || formatMessage({ id: 'auth.oauth.emailValidation.error' }),
        })
      },
    },
  })

  const { mutate: resendEmail, isPending: isResending } = useAccountEmailVerifyResendCreate({
    mutation: {
      onSuccess: () => {
        setStatus({
          type: 'success',
        })
      },
      onError: (err: AxiosError) => {
        setStatus({
          type: 'error',
          message:
            getAxiosErrorMessage(err) || formatMessage({ id: 'auth.oauth.emailValidation.error' }),
        })
      },
    },
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTimeout(() => {
      validateField(name as PasswordResetFormField, value)
    }, 300)
  }

  const onSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    const isValid = validate(formValues)
    setStatus(null)

    if (isValid) {
      changeEmail({
        data: {
          ...formValues,
          is_verification_request: true,
          back_url: pathname,
        },
      })
    }
  }

  const onResendEmail = () => {
    if (user === null) {
      return
    }

    // Use unconfirmed_email if available, otherwise use user email or formValues email
    const emailToResend =
      user.unconfirmed_email && user.unconfirmed_email.length > 0
        ? user.unconfirmed_email
        : user.email || formValues.email

    if (emailToResend.length > 0) {
      setStatus(null)
      resendEmail({
        data: {
          email: emailToResend,
        },
      })
    }
  }

  const onChangeEmailAddress = (newEmail: string) => {
    // Update form values - modal already called API and got success
    setFormValues({ email: newEmail })
  }

  useEffect(
    function syncFormEmailWithUser() {
      let initialEmail = ''
      if (user?.email != null) {
        initialEmail = user.email.endsWith(APPLE_PRIVATE_RELAY_DOMAIN) ? '' : user.email
      }
      setFormValues((prev) => ({
        ...prev,
        email: initialEmail,
      }))
    },
    [user],
  )

  // Auto-show verification pending if email was sent within last hour
  useEffect(
    function showPendingStatusIfRecentlySent() {
      if (
        user !== null &&
        !user.email_verified &&
        user.verification_email_sent_at !== null &&
        isWithinOneHour(user.verification_email_sent_at)
      ) {
        setStatus({
          type: 'success',
        })
      }
    },
    [user],
  )

  if (!isInitialized || user === null) {
    return <Loader />
  }

  // Determine placeholder text based on email type
  const isAppleRelayEmail = user.email.endsWith(APPLE_PRIVATE_RELAY_DOMAIN)
  const placeholderText = isAppleRelayEmail
    ? formatMessage({ id: 'common.labels.email' })
    : formatMessage({ id: 'common.labels.loading' })

  // Show verification pending screen after successful email send
  if (status?.type === 'success') {
    return (
      <div className="mx-auto flex max-w-[550px] flex-col gap-8">
        <div className="flex items-center justify-center">
          <div className="flex size-[80px] items-center justify-center rounded-full bg-primary-main/10 p-4 text-primary-main">
            <EnvelopeIcon style={{ width: 48, height: 48 }} />
          </div>
        </div>

        <div className="flex flex-col items-center gap-4">
          <Typography
            className="text-center"
            component="h1"
            variant="h4"
          >
            <FormattedMessage id="auth.oauth.emailValidation.successTitle" />
          </Typography>

          <div>
            <Typography
              className="text-center"
              color="text.secondary"
              variant="body1"
            >
              <FormattedMessage id="user.settings.email.verification.description" />
            </Typography>
            <Typography
              className="text-center font-semibold"
              color="text.primary"
              variant="body1"
            >
              {user.unconfirmed_email && user.unconfirmed_email.length > 0
                ? user.unconfirmed_email
                : user.email || formValues.email}
            </Typography>
          </div>
        </div>

        <div className="flex flex-col items-center gap-8">
          <Button
            className="min-w-[300px]"
            color="primary"
            disabled={isPending || isResending}
            loading={isPending || isResending}
            size="large"
            variant="contained"
            onClick={onResendEmail}
          >
            <FormattedMessage id="user.settings.email.verification.resend" />
          </Button>
          <Typography
            className="cursor-pointer font-bold"
            color="text.secondary"
            variant="h6"
            onClick={() => setShowModal(true)}
          >
            <FormattedMessage id="user.settings.email.verification.changeEmail" />
          </Typography>
        </div>

        <div className="rounded-xs bg-background-paper/50 p-4">
          <Typography
            className="text-center font-bold"
            color="text.primary"
            variant="body1"
          >
            <FormattedMessage id="user.settings.email.verification.validity" />
          </Typography>
          <Typography
            className="text-center font-bold"
            color="text.primary"
            variant="body1"
          >
            <FormattedMessage id="user.settings.email.verification.checkSpam" />
          </Typography>
        </div>

        <ChangeEmailModal
          isVerificationRequest
          backUrl={pathname}
          currentEmail={formValues.email}
          open={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={onChangeEmailAddress}
        />
      </div>
    )
  }

  return (
    <FormContainer
      className="mx-auto"
      data-tst="email-verification-form"
    >
      <div className="flex items-center justify-center">
        <div className="size-[52px] rounded-full bg-primary-main/10 p-2.5 text-primary-main">
          <EnvelopeIcon />
        </div>
      </div>
      <Typography
        className="text-center"
        variant="h4"
      >
        <FormattedMessage id="auth.oauth.emailValidation.title" />
      </Typography>

      <TextInput
        className="w-full"
        error={errors.email}
        label="common.labels.email"
        input={{
          disabled: isPending || !isInitialized,
          name: 'email',
          placeholder: placeholderText,
          type: 'email',
          value: formValues.email,
          endAddon: <IconEmail />,
          onChange,
        }}
      />
      {status?.type === 'error' && (
        <Grow
          in
          unmountOnExit
        >
          <Alert
            heading={status.message}
            severity="warning"
          />
        </Grow>
      )}
      <Button
        className="w-full"
        color="primary"
        disabled={isPending || !formValues.email || !isInitialized}
        loading={isPending || !isInitialized}
        size="large"
        type="submit"
        variant="contained"
        onClick={onSubmit}
      >
        <FormattedMessage id="common.controls.sendVerificationEmail" />
      </Button>
    </FormContainer>
  )
}
